import { observer } from 'mobx-react-lite';
import { FileProviderAriaText } from '../strings.locstring.json';
import loc, { format } from 'owa-localize';
import FileProviderView from './FileProviderView';
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import { getIconCSSForFileProvider, FileProviderIconSize } from 'owa-fileprovider-icon';
import AttachmentDataProviderScope from 'owa-service/lib/contract/AttachmentDataProviderScope';
import { isFeatureEnabled } from 'owa-feature-flags';
import * as React from 'react';
import { FileProviderViewType } from 'owa-fileprovider-store';

export interface FileProviderAndScopesViewProps {
    name: string;
    providerType: FileProviderViewType;
    onClickGenerator: (scope: AttachmentDataProviderScope) => () => void;
    scopes: AttachmentDataProviderScope[];
    isSelected?: boolean;
    selectedScope?: AttachmentDataProviderScope;
}

const classNames = require('classnames');
const styles = require('./FileProviderAndScopesView.scss');

export default observer(function FileProviderAndScopesView(props: FileProviderAndScopesViewProps) {
    const createFileProviderView = () => {
        const defaultScope: AttachmentDataProviderScope =
            props.scopes.length > 1 ? props.scopes[0] : null;
        const scopeToUse: AttachmentDataProviderScope =
            props.isSelected && props.selectedScope != null ? props.selectedScope : defaultScope;
        const providerIcon = getIconCSSForFileProvider(
            props.providerType,
            false,
            isFeatureEnabled('doc-filePicker-newNavigationHierarchy')
                ? FileProviderIconSize.Small
                : FileProviderIconSize.Medium
        );
        return (
            <FileProviderView
                name={props.name}
                icon={providerIcon}
                isSelected={props.isSelected && props.scopes.length < 2}
                ariaLabel={format(loc(FileProviderAriaText), props.name)}
                onClick={props.onClickGenerator(scopeToUse)}
            />
        );
    };
    const createFileProviderScopeList = (): JSX.Element[] | null => {
        //We only want to show the scopes if there are at least two to show.
        if (props.scopes.length < 2) {
            return null;
        }
        return props.scopes.map((scope, index) => {
            const filePickerScopeViewStyles = classNames([
                isFeatureEnabled('doc-filePicker-newNavigationHierarchy')
                    ? styles.scopeNewNav
                    : styles.scope,
                {
                    [isFeatureEnabled('doc-filePicker-newNavigationHierarchy')
                        ? styles.scopeSelectedNewNav
                        : styles.scopeSelected]:
                        props.isSelected && props.selectedScope.DisplayName === scope.DisplayName,
                },
            ]);
            const providerAndScopeName = `${props.name}, ${scope.DisplayName}`;
            const ariaProps: AriaProperties = {
                role: AriaRoles.button,
                label: format(loc(FileProviderAriaText), providerAndScopeName),
            };
            return (
                <div
                    key={index}
                    className={filePickerScopeViewStyles}
                    onClick={props.onClickGenerator(scope)}
                    data-is-focusable={true}
                    {...generateDomPropertiesForAria(ariaProps)}>
                    {scope.DisplayName}
                </div>
            );
        });
    };
    const showScopes = props.scopes.length > 1;
    return (
        <div>
            {createFileProviderView()}

            {showScopes && (
                <div className={styles.scopesContainer}> {createFileProviderScopeList()} </div>
            )}
        </div>
    );
});
